import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import { ChalkActionButton } from "../components/chalk/primitives";

// Staff invite landing: /staff/accept?token=...
export default function StaffAccept() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, switchRole, refresh } = useAuth();
  const { refresh: refreshTeams, setActive } = useTeam();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.previewStaffInvite(token).then(setInvite).catch((e) => setError(e.message || "This invite link is no longer valid."));
  }, [token]);
  useEffect(() => {
    if (user && (sessionStorage.getItem("chalkos.afterLogin") || "").startsWith("/staff/accept")) sessionStorage.removeItem("chalkos.afterLogin");
  }, [user]);

  async function accept() {
    setBusy(true);
    try {
      const d = await api.acceptStaffInvite(token);
      if (d.suggest_role) { await switchRole(d.suggest_role); await refresh(); }
      localStorage.setItem("chalkos.activeTeam", d.team_id);
      await refreshTeams();
      setActive(d.team_id);
      toast.success(d.suggest_role
        ? `You're on the ${d.team_name} staff. Switch views anytime in More.`
        : `You're on the ${d.team_name} staff.`);
      navigate("/app", { replace: true });
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  }

  function auth(path) {
    sessionStorage.setItem("chalkos.afterLogin", `/staff/accept?token=${encodeURIComponent(token)}`);
    navigate(path);
  }

  return (
    <div className="chalk-min-h-screen chalk-on-dark" style={{ background: "var(--chalk-board)", color: "var(--chalk-white)", padding: "56px 22px" }}>
      <div style={{ maxWidth: 440, margin: "0 auto" }} data-testid="staff-accept-page">
        <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.7 }}>ChalkOS · Team staff</div>
        {error ? (
          <>
            <h1 style={{ fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)", fontSize: 40, margin: "8px 0" }}>Invite expired</h1>
            <p data-testid="staff-accept-error" style={{ fontSize: 15, opacity: 0.85, lineHeight: 1.5 }}>{error}</p>
          </>
        ) : !invite ? null : (
          <>
            <h1 style={{ fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)", fontSize: 40, margin: "8px 0" }} data-testid="staff-accept-title">
              Join {invite.team_name} as {invite.label}
            </h1>
            <p style={{ fontSize: 15, opacity: 0.85, lineHeight: 1.5 }}>
              {invite.coach_name ? `Coach ${invite.coach_name}` : "The coach"} invited you to help run the team. This invite is for{" "}
              <b data-testid="staff-accept-email-hint">{invite.email_hint}</b>.
            </p>
            {loading ? null : user ? (
              <ChalkActionButton testId="staff-accept-btn" label="Accept invite" loading={busy} onClick={accept} />
            ) : (
              <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
                <ChalkActionButton testId="staff-accept-signup" label="Create a free account" onClick={() => auth("/signup?role=guardian")} />
                <ChalkActionButton testId="staff-accept-signin" variant="ghost" label="I already have an account" onClick={() => auth("/login")} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
